import { ToDo } from "../../Components/ToDo";
import { DateTimeEditInput } from "../../Components/Utils/DateSlider";
import { TimeUtils } from "../../Utils/TimeUtils";
import { Step } from "../Step/Step";
import { Cash } from "../Step/Cash";
import { StepEvent, StepEventType } from "../Step/StepEvent";
import { StepType } from "../Step/StepType";
import { AdminPersonalReturnInfo, PersonalReturnInfo } from "./Components/PersonalReturnInfo";
import { PersonalReturnStepData } from "./Models/PersonalReturnStepData";
import { PersonalReturnStepState } from "./Models/PersonalReturnStepState";
import { Line, TextInput } from "@tblabs/truffle";
import { BasketCollectionPrinter } from "../PackageToCustomer/Components/BasketCollectionPrinter";
import { DateTimeProvider } from "../../Services/DateTimeProvider";
import moment from "moment";
import { Important } from "../../Components/Utils/Important";
import { CashType } from "../Step/CashType";


export class PersonalReturnStep extends Step<PersonalReturnStepState>
{
    public Type = StepType.PersonalReturn;
    public Icon = "🐥";
    public TitleForCustomer = () => `Zwrot osobisty`;
    public Title = () => `Zwrot osobisty`;

    public InitialState = PersonalReturnStepState.NotPlannedYet;
    public WellDoneStates = [PersonalReturnStepState.MeetingOk, PersonalReturnStepState.Omitted];
    public FailedDoneStates = [PersonalReturnStepState.Canceled, PersonalReturnStepState.CanceledDespitePlanned];

    public get AsEvents(): StepEvent[]
    {
        return [
            new StepEvent(StepEventType.PersonalReturn, this.Order.Id, "🐥", "Zwrot", this.Data.PlannedMeetingTime.value, this.IsDone)
        ];
    }
    public get Mana(): Cash[]
    {
        const cash: Cash[] = [];

        if (this.Data.State.Is(PersonalReturnStepState.MeetingOk))
        {
            if (this.Data.CashToReturn.value > 0)
                cash.push(new Cash(CashType.ReturnedDeposit, this, this.Data.CashToReturn.value, "Zwrot kasy podczas zwrotu sprzętu", this.Data.MeetingTime.value))

            if (this.Data.ExtraCashToPay.value > 0)
                cash.push(new Cash(CashType.ReceivedIncome, this, this.Data.ExtraCashToPay.value, "Dopłata podczas zwrotu sprzętu", this.Data.MeetingTime.value))
        }

        return cash;
    }
    public get WhenPlanned(): Date
    {
        return this.Data.PlannedMeetingTime.value;
    }
    public get WhenDone(): Date
    {
        return new Date(+this.Data.MeetingTime.value || +this.Data.PlannedMeetingTime.value);
    }

    constructor(public Data: PersonalReturnStepData)
    {
        super(Data);

        if (!this.IsDone)
        {
            if (moment(this.Data.PlannedMeetingTime.value).isBefore(moment(DateTimeProvider.Now)))
                this.GoTo(PersonalReturnStepState.TimeToMeet);
        }
    }

    public Init(): void
    { }

    public STATES = {
        [PersonalReturnStepState.NotPlannedYet]: {
            StateName: "Jeszcze nie zaplanowane",
            Timeout: () => this.Data.PlannedMeetingTime,
            Customer: {
                Content: () => [`Spotkanie nie zostało jeszcze zaplanowane.\nJeśli zbliża się termin, możesz do nas zadzwonić i ustalić dogodny czas`],
            },
            Admin: {
                Content: () => [
                    new BasketCollectionPrinter(this.Order),
                    "Zwrot od: ", this.Order.PeopleAsString,
                    new Line(),
                    `Czas spotkania nie został jeszcze zaplanowany`,
                    new ToDo(
                        `Ustal miejsce i czas spotkania razem z klientem`,
                        new TextInput(this.Data.MeetingPlace).Placeholder("Miejsce spotkania").MarginBottom(4),
                        new DateTimeEditInput(this.Data.PlannedMeetingTime))],
                Actions: {
                    "✅": () => this.GoTo(PersonalReturnStepState.Planned),
                    "❌": () => this.GoTo(PersonalReturnStepState.Canceled),
                },
            },
        },
        [PersonalReturnStepState.Planned]: {
            StateName: "Zaplanowane",
            Timeout: () => this.Data.PlannedMeetingTime,
            Customer: {
                Content: () => [
                    `☑️ Plan spotkania:`,
                    new PersonalReturnInfo(this.Data)],
            },
            Admin: {
                Content: () => [
                    new BasketCollectionPrinter(this.Order),
                    `☑️ Zaplanowane`,
                    new AdminPersonalReturnInfo(this.Order, this.Data),
                    new ToDo(
                        `Czy spotkanie się odbyło?`)
                ],
                Actions: {
                    "✅": () => { this.Data.MeetingTime.value = DateTimeProvider.Now; this.GoTo(PersonalReturnStepState.MeetingOk); },
                    "🕑": () => this.GoTo(PersonalReturnStepState.NotPlannedYet),
                    "❌": () => this.GoTo(PersonalReturnStepState.CanceledDespitePlanned),
                }
            }
        },
        [PersonalReturnStepState.TimeToMeet]: {
            StateName: "Minął czas spotkania",
            Timeout: () => this.Data.PlannedMeetingTime,
            Customer: {
                Content: () => [
                    new Important(`Czas na spotkanie minął ${TimeUtils.TimeLeftTo(this.Data.PlannedMeetingTime)}.\nUmów nowe spotkanie by zwrócić sprzęt, a jeśli chcesz przedłużyć rezerwację - daj nam znać telefonicznie`),
                ],
            },
            Admin: {
                Content: () => [
                    new BasketCollectionPrinter(this.Order),
                    `Spotkanie nie odbyło się choć było zaplanowane na ${TimeUtils.FormattedDateTime(this.Data.PlannedMeetingTime)}`,
                    new AdminPersonalReturnInfo(this.Order, this.Data),
                    new ToDo(
                        `Ustal nowy termin spotkania lub anuluj`)
                ],
                Actions: {
                    "🕑": () => this.GoTo(PersonalReturnStepState.NotPlannedYet),
                    "❌": () => this.GoTo(PersonalReturnStepState.CanceledDespitePlanned),
                }
            }
        },
        [PersonalReturnStepState.MeetingOk]: {
            StateName: "Po spotkaniu",
            Customer: {
                Content: () => [`✅ Już po spotkaniu`],
            },
            Admin: {
                Content: () => [`✅ Spotkanie poszło dobrze`, new AdminPersonalReturnInfo(this.Order, this.Data)],
            }
        },
        [PersonalReturnStepState.Canceled]: {
            StateName: "Odwołane",
            Customer: {
                Content: () => [`❌ Spotkanie zostało anulowane`],
            },
            Admin: {
                Content: () => [`❌ Spotkanie zostało anulowane zanim się odbyło`],
            }
        },
        [PersonalReturnStepState.CanceledDespitePlanned]: {
            StateName: "Odwołane pomimo zaplanowania",
            Customer: {
                Content: () => [`❌ Spotkanie zostało odwołane`],
            },
            Admin: {
                Content: () => [`❌ Spotkanie zostało odwołane pomimo zaplanowania na ${TimeUtils.FormattedDateTime(this.Data.PlannedMeetingTime)}`],
            }
        },
        [PersonalReturnStepState.Omitted]: {
            StateName: "Pominięte",
            Customer: {
                Content: () => [`☑️ Spotkanie zostało pominięte`],
            },
            Admin: {
                Content: () => [`☑️ Spotkanie zostało pominięte`],
            }
        }
    };
}
